import { z } from 'zod'
import type { AccessLevel } from './access.ts'

/**
 * Комментарии команды к строке раздела «Салоны»: о чём договорились, когда перезвонить.
 * Пишутся на владельца (Manaro users.id); salonId — салон строки, null — регистрация без салона.
 * Комментарий без салона виден и на строке салона, который владелец заведёт потом.
 */
export const SALON_NOTE_MAX = 2000

const noteText = z
  .string()
  .trim()
  .min(1, 'Напишите комментарий.')
  .max(SALON_NOTE_MAX, `Не длиннее ${SALON_NOTE_MAX} символов.`)
  // Postgres text cannot hold NUL; without this a pasted control character turns into a 500.
  .refine((text) => !text.includes('\u0000'), 'В тексте есть невидимый служебный символ — перепечатайте его.')

const manaroId = z.number().int().positive().max(2_147_483_647)

/**
 * Напоминание автору комментария: в этот день (по Киеву) бэк-офис покажет ему окно.
 * Не раньше сегодняшнего дня и не дальше чем через год — сервер проверяет по киевской дате.
 */
export const REMINDER_MAX_DAYS = 366
export const REMINDER_TIMEZONE = 'Europe/Kyiv'

const reminderDate = z.iso.date({ error: 'Укажите дату напоминания.' })

export const salonNoteSchema = z.object({
  id: z.uuid(),
  userId: z.number(),
  salonId: z.number().nullable(),
  text: z.string(),
  authorEmail: z.string(),
  authorName: z.string(),
  createdAt: z.iso.datetime(),
  editedAt: z.iso.datetime().nullable(),
  /** День напоминания автору, YYYY-MM-DD. */
  remindOn: z.iso.date().nullable().default(null),
  /** Автор отметил «Готово». */
  reminderDoneAt: z.iso.datetime().nullable().default(null),
})

export const salonNotesResponseSchema = z.object({
  /** false — база комментариев не подключена: колонка скрыта. */
  available: z.boolean(),
  /** Сначала новые. */
  notes: z.array(salonNoteSchema),
  /** true — пришли не все: самые старые за пределом SALON_NOTES_LIST_LIMIT. */
  truncated: z.boolean().default(false),
})

export const SALON_NOTES_LIST_LIMIT = 5_000

export const salonNoteCreateRequestSchema = z.object({
  userId: manaroId,
  salonId: manaroId.nullable(),
  text: noteText,
  remindOn: reminderDate.nullable().optional(),
})

/** Любое из полей: текст, день напоминания (null — снять) или отметка «Готово». Менять может только автор. */
export const salonNoteUpdateRequestSchema = z
  .object({
    text: noteText.optional(),
    remindOn: reminderDate.nullable().optional(),
    reminderDone: z.literal(true).optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), 'Нечего менять.')
  .refine((patch) => !(patch.reminderDone && patch.remindOn !== undefined), 'Либо новая дата, либо «Готово».')

/** Напоминания того, кто спрашивает, на сегодня и просроченные — для окна в любом разделе. */
export const salonRemindersResponseSchema = z.object({
  available: z.boolean(),
  /** Сегодня по Киеву, YYYY-MM-DD. */
  today: z.iso.date(),
  /** Сначала самые давние. */
  due: z.array(salonNoteSchema),
})

/** Поддержка правит и удаляет только свои комментарии; владелец сервиса может убрать любой — например, с лишними личными данными. */
export const canDeleteAnySalonNote = (access: AccessLevel): boolean => access === 'owner'

export type SalonNote = z.infer<typeof salonNoteSchema>
export type SalonNotesResponse = z.infer<typeof salonNotesResponseSchema>
export type SalonNoteCreateRequest = z.infer<typeof salonNoteCreateRequestSchema>
export type SalonNoteUpdateRequest = z.infer<typeof salonNoteUpdateRequestSchema>
export type SalonRemindersResponse = z.infer<typeof salonRemindersResponseSchema>
